import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import { Modal, Input, Button } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import {
  capitalizeEachFirstLetter,
  fixAfterDecimal,
  getCurrencyInfo,
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  BalanceDataType,
  BankCardDataType,
} from "src/lib/constants/table-type";
import useAuth from "src/hooks/use-auth";
import { EditIcon } from "../icons/edit-icon";

type UserBalancesPageProps = {
  user: User;
};

const UserBalancesPage: FC<UserBalancesPageProps> = ({ user }) => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<BalanceDataType | null>(
    null,
  );
  // const [error, setError] = useState("");

  const [amount, setAmount] = useState("");
  const { getCurrrentUser } = useAuth();
  const permissions = getCurrrentUser().permissions;
  const hasUnfreezePermission = permissions?.some(
    (p: any) => p.name === "unfreeze balance",
  );
  const [dataSource, setDataSource] = useState<BalanceDataType[]>([]);
  const [modalAction, setModalAction] = useState<
    "edit" | "removeFrozen" | "moveMain"
  >("edit");

  const { mutate: getBalances, isLoading } = useMutation(
    user.is_business
      ? client.users.getBusinessBalances
      : client.users.getIndividualBalances,
    {
      onSuccess: (data) => {
        setDataSource(
          data.wallets.map((item) => ({
            key: item.id,
            wallet_id: item.id,
            currency: item.asset.code,
            amount: item.total,
            availableBalance: item.balance,
            frozenBalance: item.frozen_balance,
            currency_id: item.asset.id,
            name: item.asset.name,
            img_path: item.asset.img_path,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateBalances, isLoading: isUpdating } = useMutation(
    client.users.updateFrozenBalances,
    {
      onSuccess: (data) => {
        getBalances(user.id);
        setIsModalOpen(false);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: removeFrozenBalances, isLoading: isRemoving } = useMutation(
    client.users.removeFrozenBalances,
    {
      onSuccess: (data) => {
        getBalances(user.id);
        setIsModalOpen(false);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: removeMainBalances, isLoading: isRemovingMain } = useMutation(
    client.users.removeMainBalances,
    {
      onSuccess: (data) => {
        getBalances(user.id);
        setIsModalOpen(false);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const handleSubmit = (type: string) => {
    if (!selectedWallet) return; // prevent null error

    // logger.log("Updated Amount:", amount);
    // logger.log("Wallet:", selectedWallet);
    const payload = {
      asset_id: selectedWallet.currency_id as number,
      user_id: user.id,
      amount: Number(amount), // FIX: Convert string → number
    };
    if (type === "edit") {
      updateBalances(payload);
    } else if (type === "removeFrozen") {
      removeFrozenBalances(payload);
    } else if (type === "moveMain") {
      removeMainBalances(payload);
    }
  };

  const handleFrozenToMain = (record: BalanceDataType) => {
    // logger.log("selected record data", record);
    setSelectedWallet(record);
    setModalAction("edit");

    setAmount(fixAfterDecimal(record.frozenBalance || 0, 2)?.toString() || "");
    setIsModalOpen(true);
  };
  //                      fixAfterDecimal(selectedWallet?.availableBalance || 0, 2),

  const handleRemoveFrozen = (record: BalanceDataType) => {
    // logger.log("selected record data", record);
    setSelectedWallet(record);
    setModalAction("removeFrozen"); // 👈 important

    setAmount(fixAfterDecimal(record.frozenBalance || 0, 2)?.toString() || "");
    setIsModalOpen(true);
  };
  const handleRemoveMain = (record: BalanceDataType) => {
    setSelectedWallet(record);
    setModalAction("moveMain");

    setAmount(
      fixAfterDecimal(record.availableBalance || 0, 2)?.toString() || "",
    );
    setIsModalOpen(true);
  };

  useEffect(() => {
    isMounted && getBalances(user.id);
  }, [isMounted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!amount) return;

      const num = Number(amount);

      // Format only if the number differed from string (trailing zeros case)
      if (num.toString() !== amount) {
        setAmount(num.toString());
      }
    }, 600); // 600ms debounce

    return () => clearTimeout(handler);
  }, [amount]);

  const columns: ColumnsType<BalanceDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      className: "w-auto",
      key: "currency",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <div className="w-[24px] h-[24px]">{getCurrencyInfo(value).icon}</div>
          <div className="flex flex-col flex-shrink">
            <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
              {value.toUpperCase()}
            </p>
            <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
              {getCurrencyInfo(value).descr}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Available Balance
        </p>
      ),
      dataIndex: "availableBalance",
      key: "availableBalance",
      className: "w-[100px]",
      render: (value: number) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Frozen Balance
        </p>
      ),
      dataIndex: "frozenBalance",
      key: "frozenBalance",
      className: "w-[100px]",
      render: (value: number) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Total Balance
        </p>
      ),
      dataIndex: "amount",
      key: "amount",
      className: "w-[100px]",
      render: (value: number) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {fixAfterDecimal(value, 2)}
        </p>
      ),
    },
    ...(hasUnfreezePermission
      ? [
          {
            title: () => (
              <p className="font-manrope text-secondary text-[14px] font-medium">
                Action
              </p>
            ),
            key: "action",
            render: (_: any, record: BalanceDataType) => (
              <div className="flex items-center gap-2">
                <Button
                  type="default"
                  size="small"
                  onClick={() => handleFrozenToMain(record)}
                >
                  Move frozen to main
                </Button>

                <Button
                  // danger
                  size="small"
                  onClick={() => handleRemoveFrozen(record)}
                >
                  Remove frozen
                </Button>

                <Button size="small" onClick={() => handleRemoveMain(record)}>
                  Move main to frozen
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
        />
      </div>
      <Modal
        title={
          modalAction === "edit"
            ? "Move frozen to main Balance"
            : modalAction === "removeFrozen"
              ? "Remove Frozen Balance"
              : "Move Main to frozen Balance"
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <div className="flex flex-col gap-4">
          <p className="font-manrope text-primary text-[14px]">
            Currency: <b>{selectedWallet?.currency}</b>
          </p>

          <Input
            value={amount}
            onChange={(e) => {
              let value = e.target.value;

              // Block negative
              if (value.startsWith("-")) return;

              // Allow only digits & single decimal
              if (!/^\d*\.?\d*$/.test(value)) return;

              let [intPart, decimalPart] = value.split(".");

              // Limit decimal digits
              if (decimalPart && decimalPart.length > 8) return;

              // Fix leading zeros
              if (
                intPart.length > 1 &&
                intPart.startsWith("0") &&
                value[1] !== "."
              ) {
                intPart = String(Number(intPart));
                value = decimalPart ? `${intPart}.${decimalPart}` : intPart;
              }

              // Prevent exceeding frozen balance
              const numericValue = Number(value);
              const maxAmount =
                modalAction === "moveMain"
                  ? Number(
                      fixAfterDecimal(selectedWallet?.availableBalance || 0, 2),
                    )
                  : Number(
                      fixAfterDecimal(selectedWallet?.frozenBalance || 0, 2),
                    );

              if (numericValue > maxAmount) {
                value = maxAmount.toString();
              }

              setAmount(value);
            }}
            placeholder="Enter amount"
            type="number"
          />

          {/* {error && (
            <p className="text-red-500 text-[13px] font-manrope">{error}</p>
          )} */}

          <button
            onClick={() => handleSubmit(modalAction)}
            disabled={
              Number(amount) <= 0 || isUpdating || isRemoving || isRemovingMain
            }
            className={`w-full rounded-[8px] py-2 font-manrope
        ${
          Number(amount) <= 0
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-primary text-white"
        }
      `}
          >
            {modalAction === "edit"
              ? "Move Balance"
              : modalAction === "moveMain"
                ? "Move Balance"
                : "Remove Balance"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UserBalancesPage;
